import { useState, useEffect, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import type { AuthenticatedUser } from '@mem-ai-hub/shared';
import { loginRequest, apiScopes } from './msalConfig';

interface UseAuthReturn {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessDenied: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

export function useAuth(): UseAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] ?? null);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const isAuthenticated = accounts.length > 0;

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) {
      throw new Error('No active account. Please sign in.');
    }

    const response = await instance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return response.accessToken;
  }, [instance, account]);

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  // Fetch user profile from backend once authenticated
  useEffect(() => {
    if (!isAuthenticated || !account || inProgress !== 'none') {
      setUser(null);
      setAccessDenied(false);
      setIsLoading(inProgress !== 'none');
      return;
    }

    let cancelled = false;

    async function fetchProfile() {
      try {
        setIsLoading(true);
        const token = await getAccessToken();
        const response = await fetch('/api/user', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 403) {
          if (!cancelled) {
            setAccessDenied(true);
            setUser(null);
          }
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch profile: ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setAccessDenied(false);
          setUser(data.data ?? data);
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        if (!cancelled) {
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, getAccessToken, inProgress]);

  return {
    user,
    isAuthenticated,
    isLoading,
    accessDenied,
    login,
    logout,
    getAccessToken,
  };
}
